import { Injectable } from '@angular/core';
import { lastValueFrom, map } from 'rxjs';
import { AppDialogService } from 'src/app/services/dialog.service';
import {
  isImageProportionWithinPercentMargin,
  isSvgUploadFile
} from 'src/app/utils/image-upload.util';
import { FileControllerService } from '../admin-api';
import { LoaderService } from './loader.service';

@Injectable({
  providedIn: 'root'
})
export class ImageService {
  constructor(private fileService: FileControllerService) {}

  async validateImageDimensions(
    file: File,
    expectedRatio: number,
    maxWeight?: number,
    allowedTypes: string[] = [
      'image/webp',
      'image/png',
      'image/jpeg',
      'image/gif',
      'image/svg+xml',
      'image/svg'
    ],
    percentMargin?: number,
    expectedWidth?: number,
    expectedHeight?: number
  ): Promise<boolean> {
    let error: string = '';

    if (!allowedTypes.includes(file.type) && !isSvgUploadFile(file))
      error += 'Formato de imagem inválido <br />';

    const actualWeight = file.size;

    if (maxWeight && actualWeight > maxWeight)
      error += `Tamanho máximo excedido em ${
        (actualWeight - maxWeight) / 1024
      }kb. <br />`;

    if (isSvgUploadFile(file)) {
      if (error !== '') {
        AppDialogService.showErrorDialog({
          message: error
        });
        return false;
      }
      return true;
    }

    const img = new Image();
    const objectUrl = URL.createObjectURL(file);
    img.src = objectUrl;

    await new Promise<void>((resolve) => {
      img.onload = () => {
        const width = img.width;
        const height = img.height;

        const isRatioValid =
          percentMargin === undefined
            ? Math.abs(width / height - expectedRatio) < 0.01
            : isImageProportionWithinPercentMargin(
                width,
                height,
                expectedWidth ?? expectedRatio,
                expectedHeight ?? 1,
                percentMargin
              );

        if (!isRatioValid) {
          error += 'A imagem selecionada não possui as dimensões solicitadas.';
        }

        URL.revokeObjectURL(objectUrl);
        resolve();
      };
      img.onerror = () => {
        error += 'Não foi possível ler a imagem selecionada. <br />';
        URL.revokeObjectURL(objectUrl);
        resolve();
      };
    });
    if (error !== '') {
      AppDialogService.showErrorDialog({
        message: error
      });
      return false;
    }
    return true;
  }

  async uploadImage(
    file: File,
    fileName: string,
    filePath: string
  ): Promise<string> {
    LoaderService.showLoader();

    const blob = file.slice(0, file.size, file.type);
    const uploadFile = new File([blob], fileName, { type: file.type });

    try {
      const result = (await lastValueFrom(
        this.fileService
          .uploadFileForm(filePath, uploadFile, uploadFile.name)
          .pipe(map((data) => data.result))
      )) as string;
      return result;
    } catch (error: any) {
      AppDialogService.showErrorDialog(error);
    }
    LoaderService.showLoader(false);
  }
}
